import type { TicketListViewModel } from "@lumora/core";
import type { TicketType } from "@lumora/bridge";
import { useViewModel } from "@lumora/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

export interface TicketsSectionProps {
  viewModel: TicketListViewModel;
}

/**
 * TicketsSection renders the support-ticket list and a create form.
 * Zero business logic — all state and commands are delegated to TicketListViewModel.
 */
export function TicketsSection({ viewModel }: TicketsSectionProps) {
  const state = useViewModel(viewModel);

  const [type, setType] = useState<TicketType>("issue");
  const [description, setDescription] = useState("");

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void viewModel.create({ type, description }).then((ok) => {
      if (ok) {
        setDescription("");
        setType("issue");
      }
    });
  }

  return (
    <section className="portal-section" data-lumora-view="tickets">
      <h2>Support Tickets</h2>
      <p className="portal-notice portal-tickets-note">
        Product ideas with GitHub tracking:{" "}
        <Link to="/__/request">Submit a feature request</Link>. Use tickets below for support.
      </p>

      {state.error ? <p data-lumora-error className="portal-error">{state.error}</p> : null}

      {state.loading && state.tickets.length === 0 ? (
        <p>Loading tickets…</p>
      ) : state.tickets.length === 0 ? (
        <p className="portal-notice">No tickets yet.</p>
      ) : (
        <table className="portal-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created</th>
            </tr>
          </thead>
          <tbody>
            {state.tickets.map((ticket) => (
              <tr key={ticket.id} data-status={ticket.status}>
                <td>{ticket.displayType}</td>
                <td>{ticket.description}</td>
                <td>{ticket.isOpen ? "Open" : "Closed"}</td>
                <td>{new Date(ticket.createdAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form className="portal-form" onSubmit={handleSubmit}>
        <h3>New Ticket</h3>
        <label>
          Type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as TicketType)}
            disabled={state.loading}
          >
            <option value="issue">Issue</option>
            <option value="feature">Feature Request</option>
          </select>
        </label>
        <label>
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={state.loading}
            rows={4}
            placeholder="Describe the issue or request…"
          />
        </label>
        <button type="submit" className="portal-btn" disabled={state.loading || description.trim() === ""}>
          {state.loading ? "Submitting…" : "Submit Ticket"}
        </button>
      </form>
    </section>
  );
}
